import { z } from 'zod';
import {
  CursorInputSchema,
  IdSchema,
  IdempotencyKeySchema,
  IsoDateTimeSchema,
  MoneySchema,
  pageOf,
  withMeta,
} from '../common';
import { defineProcedures, mutation, query, type RouterShape } from '../procedure';
import { defineFamily } from './family';

/** admin.* — kernel router (role admin): feature flags / kill switches, usage + cost dashboard (§20.6), dead-letter review
 *  (§15.2), audit trail. Backs the /dev/console surface in Phase 1 (excluded from production builds). */

export const FlagSchema = z
  .object({
    key: z.string(),
    enabled: z.boolean(),
    description: z.string(),
    killSwitch: z.boolean(),
    updatedAt: IsoDateTimeSchema,
  })
  .strict();
export const UsageRowSchema = z
  .object({
    workload: z.string(),
    calls: z.number().int(),
    tokens: z.number().int(),
    cost: MoneySchema,
    costPerWau: MoneySchema,
    alert: z.boolean(),
  })
  .strict();
export const DeadLetterSchema = z
  .object({
    id: IdSchema,
    eventId: IdSchema,
    type: z.string(),
    attempts: z.number().int(),
    error: z.string(),
    ageS: z.number().int(),
    firstFailedAt: IsoDateTimeSchema,
  })
  .strict();
export const AuditEventSchema = z
  .object({
    id: IdSchema,
    at: IsoDateTimeSchema,
    actor: z.string(),
    action: z.string(),
    target: z.string(),
    hash: z.string(),
    prevHash: z.string().nullable(),
  })
  .strict();

export const adminProcedures = defineProcedures({
  'admin.flags.list': query(
    'Feature flags + kill switches',
    z.void(),
    withMeta({ items: z.array(FlagSchema) }),
  ),
  'admin.flags.set': mutation(
    'Set a flag / kill switch',
    z.object({ idempotencyKey: IdempotencyKeySchema, key: z.string(), enabled: z.boolean() }).strict(),
    withMeta({ items: z.array(FlagSchema) }),
  ),
  'admin.usage.summary': query(
    'AI/voice usage + cost per workload',
    z.void(),
    withMeta({ rows: z.array(UsageRowSchema), period: z.string() }),
  ),
  'admin.dlq.list': query('Dead-letter queue', CursorInputSchema.optional(), pageOf(DeadLetterSchema)),
  'admin.dlq.retry': mutation(
    'Retry a dead-lettered event',
    z.object({ idempotencyKey: IdempotencyKeySchema, id: IdSchema }).strict(),
    withMeta({ ok: z.literal(true) }),
  ),
  'admin.audit.list': query(
    'Hash-chained audit events',
    CursorInputSchema.optional(),
    pageOf(AuditEventSchema),
  ),
});
export type AdminFamilyClient = RouterShape<typeof adminProcedures>;

export const adminFamily = defineFamily({
  name: 'admin',
  title: 'Admin (kernel)',
  screens: ['/dev/console (dev only)'],
  procedures: adminProcedures,
  binding: 'simulated',
});
